import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_CANCEL_GENERATE_AI_POST, API_KEY } from '../../utils/constants';
import { useSelector, useDispatch } from 'react-redux';
import { authSelector } from '../../store/slices/auth';
import { setSelectedClient } from '../../store/slices/selectedClient';
import { Button } from '../../components/ui/button';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { useSidebar } from '../../contexts/SidebarContext';

export default function Countdown({
  selectedClient,
  generationStartTime,
  onComplete
}) {
  const { userInfo } = useSelector(authSelector);
  const { sidebarOpen } = useSidebar();
  const dispatch = useDispatch();

  // Use the passed start timestamp as prop, or current as fallback
  const generationStartedAtMs = generationStartTime || Date.now();
  const targetTime = generationStartedAtMs + 9 * 60 * 1000;

  const [timeLeft, setTimeLeft] = useState(targetTime - Date.now());
  const [isComplete, setIsComplete] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = targetTime - Date.now();
      setTimeLeft(remaining);
      if (remaining <= 0) {
        // After 9 minutes, check if generatingContentByAI is false
        if (selectedClient?.generatingContentByAI === false) {
          setIsComplete(true);
          clearInterval(interval);
        }
        // If still true, keep showing counter at 00:00
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetTime, selectedClient?.generatingContentByAI]);

  // Function to format remaining time
  const formatTime = (ms) => {
    if (ms <= 0) {
      return '00:00';
    }

    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Function to reload the page
  const handleReload = () => {
    if (onComplete) {
      onComplete();
    }
    window.location.reload();
  };

  // Function to show cancel confirmation
  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  // Function to cancel the AI generation after confirmation
  const handleCancelConfirm = async () => {
    if (isCancelling) return; // Prevent multiple clicks

    setIsCancelling(true);
    setShowCancelConfirm(false);

    try {
      const response = await axios.post(
        API_CANCEL_GENERATE_AI_POST,
        { clientId: selectedClient.id },
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        // Update redux-persist so that after reload the recovery useEffect
        // does NOT re-trigger (generatingContentByAI must be false in localStorage)
        dispatch(
          setSelectedClient({
            ...selectedClient,
            generatingContentByAI: false
          })
        );
        await new Promise((resolve) => setTimeout(resolve, 150));
        if (onComplete) {
          onComplete();
        }
        window.location.reload();
      }
    } catch (error) {
      console.error('Error cancelling AI generation:', error);
      // Flush false to redux-persist so recovery useEffect won't re-trigger
      dispatch(
        setSelectedClient({
          ...selectedClient,
          generatingContentByAI: false
        })
      );
      if (onComplete) {
        onComplete();
      }
    } finally {
      setIsCancelling(false);
    }
  };

  // Function to cancel the cancellation
  const handleCancelDismiss = () => {
    setShowCancelConfirm(false);
  };

  return (
    <div
      className="fixed inset-0 z-10 flex items-center justify-center"
      style={{
        left: sidebarOpen ? '16rem' : '4rem',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}>
      <div className="bg-background rounded-lg shadow-xl sm:max-w-md w-full text-center py-10 px-6">
        {!isComplete ? (
          <>
            {!showCancelConfirm ? (
              <>
                <div className="items-center">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-r from-gray-700 to-gray-900 flex items-center justify-center mb-4 mx-auto">
                    <Sparkles className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">
                    Generating Content
                  </h2>
                  <p className="text-base text-muted-foreground mt-2">
                    AI is creating posts for your client
                  </p>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="text-4xl font-bold text-foreground">
                    {formatTime(timeLeft)}
                  </div>
                  <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-gray-700 to-gray-900 rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(
                            100,
                            ((9 * 60 * 1000 - timeLeft) / (9 * 60 * 1000)) * 100
                          )
                        )}%`
                      }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Estimated time remaining
                  </p>
                </div>

                <div className="mt-6">
                  <Button
                    variant="secondary"
                    className="px-8 h-11 text-base font-semibold rounded-lg"
                    onClick={handleCancelClick}
                    disabled={isCancelling}>
                    {isCancelling ? 'Cancelling...' : 'Cancel'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="items-center">
                  <h2 className="text-2xl font-bold text-foreground">
                    Cancel Generation
                  </h2>
                  <p className="text-base text-muted-foreground mt-2">
                    Are you sure you want to cancel the content generation?
                  </p>
                </div>

                <div className="flex justify-center gap-4 mt-4">
                  <Button
                    variant="outline"
                    className="px-8 h-11 text-base font-semibold rounded-lg"
                    onClick={handleCancelDismiss}>
                    Cancel
                  </Button>
                  <Button
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                    onClick={handleCancelConfirm}
                    disabled={isCancelling}>
                    {isCancelling ? 'Cancelling...' : 'OK'}
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="items-center">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4 mx-auto border-4 border-green-500">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-600">
                Content Ready!
              </h2>
              <p className="text-base text-muted-foreground mt-2">
                Your AI-generated content is complete
              </p>
            </div>

            <div className="mt-6">
              <Button
                className="w-full px-8 h-11 text-base font-semibold rounded-lg"
                onClick={handleReload}>
                <i className="fas fa-sync-alt mr-2"></i>
                Refresh Calendar
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
